import { DragEndEvent, DragOverEvent, DragStartEvent } from '@dnd-kit/core'
import { DataItem } from 'scrivito'

import { BoardSections } from '@/Components/KanbanView/types'
import {
  findBoardSectionContainer,
  getInitialOrder,
  getStatus,
  isStateAllowed,
} from '@/Components/KanbanView/util'

/**
 * custom hook to deal with the lead kanban board drag n drop events
 * @param setActiveItemId React setState function to set the dragged item
 * @param boardSections
 * @param setBoardSections
 * @returns
 */
export const useDragEvents = (
  setActiveItemId: React.Dispatch<React.SetStateAction<string | null>>,
  boardSections: BoardSections,
  setBoardSections: React.Dispatch<React.SetStateAction<BoardSections>>,
  initialItems: DataItem[],
  schema: DataItem,
  onDrop: (item: DataItem, section: string, allowed: boolean) => void,
) => {
  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveItemId(active.id as string)
  }

  const handleDragOver = ({ active, over }: DragOverEvent) => {
    // Find the containers
    const activeContainer = findBoardSectionContainer(
      boardSections,
      active.id as string,
    )
    const overContainer = findBoardSectionContainer(
      boardSections,
      over?.id as string,
    )

    if (
      !activeContainer ||
      !overContainer ||
      activeContainer === overContainer
    ) {
      return
    }

    setBoardSections((boardSection) => {
      const activeItem = (boardSection[activeContainer] || []).find(
        (item) => item.id() === active.id,
      ) as DataItem

      return {
        ...boardSection,
        [activeContainer]: (boardSection[activeContainer] || []).filter(
          (item) => item.id() !== active.id,
        ),
        [overContainer]: (boardSection[overContainer] || []).concat(activeItem),
      }
    })
  }

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    const activeContainer = findBoardSectionContainer(
      boardSections,
      active.id as string,
    )
    const overContainer = findBoardSectionContainer(
      boardSections,
      over?.id as string,
    )

    if (
      !activeContainer ||
      !overContainer ||
      activeContainer !== overContainer
    ) {
      return
    }

    const activeItem = (boardSections[activeContainer] || []).find(
      (item) => item.id() === active.id,
    ) as DataItem

    const allowed = isStateAllowed(activeItem, overContainer, schema)

    if (allowed) {
      const orderedSection = getInitialOrder(
        boardSections[overContainer] || [],
        initialItems,
      )

      setBoardSections((boardSection) => ({
        ...boardSection,
        [overContainer]: orderedSection,
      }))
    } else {
      // move back to original container
      const originalContainer = getStatus(activeItem)
      const originalSection = getInitialOrder(
        (boardSections[originalContainer] || []).concat(activeItem),
        initialItems,
      )

      setBoardSections((boardSection) => ({
        ...boardSection,
        [activeContainer]: (boardSection[activeContainer] || []).filter(
          (item) => item.id() !== active.id,
        ),
        [originalContainer]: originalSection,
      }))
    }

    setActiveItemId(null)

    onDrop(activeItem, overContainer, allowed)
  }

  return {
    handleDragStart,
    handleDragOver,
    handleDragEnd,
  }
}
